import DSBox from '../../../../design-system/DSBox';
import CollectionStatus from '../CollectionStatus';
import {
  CollectionReviewStates,
  CollectionType,
  MarketplaceStatus,
} from '../../../../api/collections';
import CollectionInfo from './CollectionInfo';
import WarningDialog from '../../../../components/WarningDialog';
import DSTabGradientViewPaper from '../../../../design-system/Paper/DSTabGradientViewPaper';
import DSCircularProgress from '../../../../design-system/DSCircularProgress';
import { useNavigate, useParams } from 'react-router-dom';
import useCollectionQuery from '../../hooks/useCollectionQuery';
import useCollectionStatsQuery from '../../hooks/useCollectionStatsQuery';
import useCollectionCountryPerformanceQuery from '../../hooks/useCollectionCountryPerformanceQuery';
import useCollectionOrdersGraphQuery from '../../hooks/useCollectionOrdersGraphQuery';
import { useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { OrderGraphPeriod } from '../../../../api/collections';
import { PopulatedKlydo } from '../../../../api/klydo';
import { hasSound } from '../../../../utils';
import { DSTypography } from '../../../../design-system/Typography';
import DSTabButton from '../../../../design-system/DSTabButton';
import DSDivider from '../../../../design-system/DSDivider';
import {
  CollectionTab,
  collectionTabLabels,
  collectionTabs,
} from '../../types/collectionTabs';
import ContentTab from './Tabs/ContentTab';
import AssetsTab from './Tabs/AssetsTab';
import { useShopifyPreviewUrlQuery } from '../../hooks/useShopifyPreviewUrlQuery';
import PreviewButton from './PreviewButton';
import EditButton from './EditButton';
import AffiliateLinkButton from '../AffiliateLinkButton';
import useUpsertCollectionMutation from '../../hooks/useUpsertCollectionMutation';
import useSendCollectionToClock from '../../hooks/useSendCollectionToClock';
import SendToClockButton from '../../../../components/SendToClockButton';
import AnalyticsTab from '../AnalyticsTab';
import MonetizationTab from '../MonetizationTab';
import useAppContext from '../../../../hooks/useAppContext';

const CollectionPreview = () => {
  const { collectionId } = useParams();
  const { user } = useAppContext();
  const {
    data: collection,
    isLoading,
    isFetching,
  } = useCollectionQuery(collectionId!);
  const { mutate: updateCollectionMutation } = useUpsertCollectionMutation();
  const sendToClock = useSendCollectionToClock(
    collection?.type === CollectionType.Approved ? collection : undefined,
  );
  const {
    data: cachedPreviewData,
    refetch: fetchPreviewUrl,
    isFetching: isFetchingPreviewUrl,
  } = useShopifyPreviewUrlQuery(collectionId, collection);
  const [selectedPeriod, setSelectedPeriod] = useState<OrderGraphPeriod>(
    OrderGraphPeriod.WEEK,
  );

  const isInMarketplace =
    collection?.marketplace?.status === MarketplaceStatus.True;

  const isAnalyticsEnabled = isInMarketplace;

  const {
    data: allTimeStatsData,
    isLoading: isAllTimeStatsLoading,
    isError: isAllTimeStatsError,
  } = useCollectionStatsQuery({
    collectionId: collectionId!,
    enabled: isAnalyticsEnabled,
  });

  const {
    data: statsData,
    isLoading: isStatsLoading,
    isError: isStatsError,
  } = useCollectionStatsQuery({
    collectionId: collectionId!,
    period: selectedPeriod,
    enabled: isAnalyticsEnabled,
  });
  const {
    data: countryPerformanceData,
    isLoading: isCountryPerformanceLoading,
    isError: isCountryPerformanceError,
  } = useCollectionCountryPerformanceQuery({
    collectionId: collectionId!,
    period: selectedPeriod,
    enabled: isAnalyticsEnabled,
  });
  const {
    data: ordersGraphData,
    isLoading: isOrdersGraphLoading,
    isError: isOrdersGraphError,
  } = useCollectionOrdersGraphQuery({
    collectionId: collectionId!,
    period: selectedPeriod,
    enabled: isAnalyticsEnabled,
  });

  const isAnalyticsLoading =
    isStatsLoading || isCountryPerformanceLoading || isOrdersGraphLoading;

  const { spacing } = useTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<CollectionTab>(
    CollectionTab.Content,
  );
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const collectionStatus = useMemo(() => {
    if (!collection) return CollectionReviewStates.Draft;

    return collection.type === CollectionType.Draft
      ? collection.reviewStatus.state
      : CollectionReviewStates.Approved;
  }, [collection]);

  const isCollectionPending =
    collectionStatus === CollectionReviewStates.Pending;

  const isCollectionRejected =
    collectionStatus === CollectionReviewStates.Rejected;

  const isMissingArtistName = !user?.name || user.name.trim() === '';

  const disabledActivePreviewNavigation = useMemo(() => {
    return (
      collection?.marketplace?.status !== MarketplaceStatus.True ||
      !collection?.productPageMetadata?.shopifyProductId
    );
  }, [collection]);

  const isAnalyticsDisabled = useMemo(() => {
    return collection?.marketplace?.status !== MarketplaceStatus.True;
  }, [collection?.marketplace]);

  const handleBackToEdit = () => {
    if (!collection) return;
    if (isCollectionPending || isCollectionRejected) {
      updateCollectionMutation({
        collectionId: collection.id,
        collection: {
          // State only: editing a rejection passes through `draft`, which must not clear the note.
          reviewStatus: { state: CollectionReviewStates.Draft },
        },
      });
      navigate(`/collection/edit/${collection.id}`);
    }
  };

  const handleCreateShopifyPreviewUrl = useCallback(async () => {
    if (!collectionId) {
      return;
    }

    let previewUrl = cachedPreviewData?.previewUrl;

    if (!previewUrl) {
      const { data } = await fetchPreviewUrl();
      previewUrl = data?.previewUrl;
    }

    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  }, [collectionId, cachedPreviewData, fetchPreviewUrl]);

  const navigateToActiveProductPage = useCallback(async () => {
    if (!collectionId) {
      return;
    }

    // The live product handle is generated/owned by Shopify, so fetch the real
    // public URL from the backend instead of guessing it from the name.
    let productUrl = cachedPreviewData?.productUrl;

    if (!productUrl) {
      const { data } = await fetchPreviewUrl();
      productUrl = data?.productUrl;
    }

    if (productUrl) {
      window.open(productUrl, '_blank');
    }
  }, [collectionId, cachedPreviewData, fetchPreviewUrl]);

  const handleKlydoCardClick = useCallback(
    (klydo: PopulatedKlydo) => {
      navigate(`/collection/${collectionId}/klydo/${klydo.id}`);
    },
    [collectionId, navigate],
  );

  useEffect(() => {
    if (isAnalyticsDisabled && activeTab === CollectionTab.Analytics) {
      setActiveTab(CollectionTab.Content);
    }
  }, [isAnalyticsDisabled, activeTab]);

  if (isLoading || isFetching) {
    return (
      <DSTabGradientViewPaper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          margin: spacing(3),
          paddingY: spacing(4),
          height: '75vh',
          gap: spacing(3),
        }}
      >
        <DSCircularProgress />
      </DSTabGradientViewPaper>
    );
  }

  if (!collection) {
    return (
      <DSTabGradientViewPaper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          margin: spacing(3),
          paddingY: spacing(4),
          height: '75vh',
        }}
      >
        <DSTypography variant="h3">Collection not found</DSTypography>
      </DSTabGradientViewPaper>
    );
  }

  return (
    <DSTabGradientViewPaper>
      <DSBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          margin: spacing(3),
          paddingY: spacing(4),
          gap: spacing(3),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: spacing(2),
            width: '100%',
          }}
        >
          <DSBox
            sx={{
              display: 'flex',
              gap: spacing(1),
              flexDirection: 'row',
              flex: '1 1 480px',
              minWidth: 0,
            }}
          >
            <DSBox
              sx={{
                display: 'flex',
                gap: spacing(2),
                flexDirection: 'row',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              <DSTypography
                variant="h3"
                sx={{
                  wordWrap: 'break-word',
                  width: '100%',
                }}
              >
                {collection.name}
              </DSTypography>
            </DSBox>
          </DSBox>
          <DSBox
            sx={{
              display: 'flex',
              gap: spacing(1),
              flexDirection: 'row',
              alignItems: 'center',
              // The buttons are nowrap, so without wrapping the row overflows
              // the card on a narrow viewport.
              flexWrap: 'wrap',
              rowGap: spacing(1),
              justifyContent: 'flex-end',
              marginLeft: 'auto',
            }}
          >
            <CollectionStatus
              state={collectionStatus}
              marketplace={collection.marketplace}
            />
            {collection.type === CollectionType.Approved &&
            collection.marketplace?.status !== MarketplaceStatus.Pending ? (
              <>
                {isInMarketplace && (
                  <AffiliateLinkButton
                    collectionId={collection.id}
                    collectionName={collection.name}
                    hasAffiliateLink={
                      !!collection.productPageMetadata
                        ?.affiliateBundleShopifyProductId
                    }
                    cartImage={
                      collection.productPageMetadata?.bundleProductCartImage
                    }
                  />
                )}
                <PreviewButton
                  onClick={
                    disabledActivePreviewNavigation
                      ? handleCreateShopifyPreviewUrl
                      : navigateToActiveProductPage
                  }
                  disabled={isFetchingPreviewUrl}
                  isLoading={isFetchingPreviewUrl}
                />
                <SendToClockButton {...sendToClock} />
              </>
            ) : collection.type === CollectionType.Approved &&
              collection.marketplace?.status ===
                MarketplaceStatus.Pending ? null : (
              <>
                <EditButton
                  onClick={
                    collection.reviewStatus.state ===
                    CollectionReviewStates.Rejected
                      ? handleBackToEdit
                      : () => setWarningDialogOpen(true)
                  }
                  variant={
                    collection.reviewStatus.state ===
                    CollectionReviewStates.Rejected
                      ? 'primary'
                      : 'secondary'
                  }
                />
                {collection.reviewStatus.state !==
                  CollectionReviewStates.Rejected &&
                  collection.marketplace?.status !==
                    MarketplaceStatus.Pending && (
                    <PreviewButton
                      onClick={handleCreateShopifyPreviewUrl}
                      disabled={isFetchingPreviewUrl || isMissingArtistName}
                      isLoading={isFetchingPreviewUrl}
                      tooltip={
                        isMissingArtistName
                          ? 'Add your artist name on the Artist page before previewing the product page.'
                          : ''
                      }
                    />
                  )}
              </>
            )}
          </DSBox>
        </DSBox>
        <CollectionInfo
          description={collection?.description}
          cover={collection?.coverUrl}
          collectionStatus={collectionStatus}
          reviewStatusMessage={collection?.reviewStatus?.message}
          labels={collection?.productPageMetadata?.labels}
          credits={collection?.productPageMetadata?.credits}
          klydoCount={collection?.klydos?.length || 0}
          hasChime={collection?.klydos.some(
            (klydo) => klydo.chimesUrls && klydo.chimesUrls.length > 0,
          )}
          hasSound={collection?.klydos.some((klydo) =>
            hasSound(klydo.capabilitiesV2?.animationSound?.mode),
          )}
          hasThirteenPlusTag={collection?.klydos.some((klydo) =>
            klydo.tags?.includes('13+'),
          )}
          hasPSFTag={collection?.klydos.some((klydo) =>
            klydo.tags?.includes('PSF'),
          )}
          hasReminder={collection?.klydos.some(
            (klydo) => !!klydo.capabilitiesV2?.alarm,
          )}
          totalPurchases={allTimeStatsData?.totalOrders || 0}
          totalSales={allTimeStatsData?.totalSales || 0}
          sponsored={collection?.sponsored}
          totalRedemptions={allTimeStatsData?.totalRedemptions || 0}
          statsLoading={isAllTimeStatsLoading && isAnalyticsEnabled}
          statsError={isAllTimeStatsError}
        />
      </DSBox>

      <DSBox
        sx={{
          display: 'flex',
          gap: spacing(1),
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'hidden',
          maxWidth: {
            xxs: '100%',
            xs: '50%',
            sm: '60%',
            md: '70%',
            lg: '80%',
          },
          paddingBottom: spacing(0.5),
          paddingX: spacing(3),
          marginTop: spacing(2),
          '&::-webkit-scrollbar': {
            height: '4px',
          },
        }}
      >
        {collectionTabs.map((tab) => (
          <DSTabButton
            key={tab}
            title={collectionTabLabels[tab]}
            isSelected={activeTab === tab}
            disabled={tab === CollectionTab.Analytics && isAnalyticsDisabled}
            onClick={() => setActiveTab(tab)}
          />
        ))}
      </DSBox>
      <DSDivider sx={{ marginTop: spacing(1), marginBottom: spacing(4) }} />

      {activeTab === CollectionTab.Content && (
        <ContentTab
          collection={collection}
          onKlydoClick={handleKlydoCardClick}
        />
      )}

      {activeTab === CollectionTab.Assets && (
        <AssetsTab
          logoUrl={collection.logoUrl}
          coverUrl={collection.coverUrl}
          thumbnailBackgroundUrl={
            collection?.productPageMetadata?.thumbnailBackgroundUrl
          }
          logoTypeURL={collection?.productPageMetadata?.logoTypeUrl}
        />
      )}

      {activeTab === CollectionTab.Monetization && (
        <MonetizationTab planId={collection?.paymentDetails?.planId} />
      )}

      {activeTab === CollectionTab.Analytics && !isAnalyticsDisabled && (
        <AnalyticsTab
          statsData={{
            totalPurchases: statsData?.totalOrders || 0,
            totalSales: statsData?.totalSales || 0,
            totalRedemptions: statsData?.totalRedemptions || 0,
          }}
          sponsored={collection?.sponsored}
          countryPerformanceData={countryPerformanceData || []}
          orderGraphData={ordersGraphData?.graphData || []}
          totalOrders={statsData?.totalOrders || 0}
          layout="row"
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          isLoading={isAnalyticsLoading}
          statsError={isStatsError}
          countryPerformanceError={isCountryPerformanceError}
          orderGraphError={isOrdersGraphError}
        />
      )}

      <WarningDialog
        warningMessage="Are you sure you want to cancel the review? This will return the collection to draft status."
        open={warningDialogOpen}
        handleConfirmationClick={() => {
          setWarningDialogOpen(false);
          handleBackToEdit();
        }}
        handleClose={() => setWarningDialogOpen(false)}
      />
    </DSTabGradientViewPaper>
  );
};

export default CollectionPreview;
